import { getElectronAPI } from "@/modules/electron/api";
import { URL } from "url";
import {
  CalendarEvent,
  RecallMeetingDetectedEventKind,
  RecallPermissionKind,
  RecallPermissionStatus,
  ElectronAppInfo,
  RecallSdkState,
  ImportAppleNotesResult,
} from "@/modules/electron/types";
import { ThemeType } from "@/mds/themes/theme-context";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { TrackedEvent } from "@/domains/metrics";

export const electronBindingNavigateToInternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToInternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingNavigateToExternalUrl = async ({ url }: { url: URL }) => {
  const electronApi = getElectronAPI();

  await electronApi?.navigateToExternalUrl({ url: url.toString() });

  return null;
};

export const electronBindingToggleMenu = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.toggleMenu();
};

export const electronBindingMinimizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.minimizeWindow();
};

export const electronBindingMaximizeWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.maximizeWindow();
};

export const electronBindingCloseWindow = async () => {
  const electronApi = getElectronAPI();

  await electronApi?.closeWindow();
};

interface ElectronBindingUpdateNowOptions {
  waitForUpdate?: boolean;
}
export const electronBindingUpdateNow = ({
  waitForUpdate = false,
}: ElectronBindingUpdateNowOptions) => {
  const electronApi = getElectronAPI();

  return electronApi?.updateNow(waitForUpdate);
};

export const electronBindingRestartApp = async () => {
  const electronApi = getElectronAPI();

  if (electronApi?.restartApp) {
    await electronApi.restartApp();
  }
};

export const electronBindingOpenSystemSoundSettings = async ({
  focus,
}: {
  focus?: "input" | "output";
} = {}) => {
  const electronApi = getElectronAPI();
  await electronApi?.openSystemSoundSettings?.({ focus });
};

export const electronBindingSetTheme = async ({ theme }: { theme: ThemeType }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setTheme({ theme });
};

export const electronBindingSetMeetingNotificationEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setMeetingNotificationEnabled?.({ enabled });
};

export const electronBindingGetMeetingNotificationEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getMeetingNotificationEnabled?.()) ?? { enabled: false };
};

export const electronBindingSetAdvancedMeetingDetectionShown = async ({
  shown,
}: {
  shown: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setAdvancedMeetingDetectionShown?.({ shown });
};

export const electronBindingGetAdvancedMeetingDetectionShown = async (): Promise<{
  shown: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getAdvancedMeetingDetectionShown?.()) ?? { shown: false };
};

export const electronBindingSetTeamsMeetingDetectionEnabled = async ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setTeamsMeetingDetectionEnabled?.({ enabled });
};

export const electronBindingGetTeamsMeetingDetectionEnabled = async (): Promise<{
  enabled: boolean;
}> => {
  const electronApi = getElectronAPI();
  return (await electronApi?.getTeamsMeetingDetectionEnabled?.()) ?? { enabled: false };
};

type BrowserMetadata = {
  segmentAnonymousId?: string;
  gaClientId?: string;
  gaSessionId?: string;
};

export const electronBindingOnBrowserMetadata = async (
  callback: (metadata: BrowserMetadata) => Promise<void>
): Promise<() => void> => {
  const electronApi = getElectronAPI();

  try {
    if (!electronApi?.onBrowserMetadata) {
      return () => undefined;
    }

    const unsubscribe = await electronApi.onBrowserMetadata(async metadata => {
      await callback({
        segmentAnonymousId: metadata.segmentAnonymousId ?? undefined,
        gaClientId: metadata.gaClientId ?? undefined,
        gaSessionId: metadata.gaSessionId ?? undefined,
      });
    });

    return unsubscribe;
  } catch (error) {
    logger.warn({
      message: "[electronBindingOnBrowserMetadata] Failed to subscribe to browser metadata.",
      info: { error: objectModule.safeErrorAsJson(error as Error) },
    });
  }

  return () => undefined;
};

export const electronBindingSetCalendarEvents = async ({ events }: { events: CalendarEvent[] }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setCalendarEvents?.({ events });
};

export const electronBindingOnTrackedEvent = async ({
  callback,
}: {
  callback: (payload: { trackedEvent: TrackedEvent }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.onTrackedEvent?.(callback);
};

export const electronBindingSetIsClientReady = async ({ isReady }: { isReady: boolean }) => {
  const electronApi = getElectronAPI();
  await electronApi?.setIsClientReady?.({ isReady });
};

// ==========================
// Recall.ai Desktop SDK Bindings
// ==========================

export const recallBindingPrepareDesktopRecording = async () => {
  const electronApi = getElectronAPI();
  return (await electronApi?.recall.prepareDesktopRecording()) || { windowId: "" };
};

export const recallBindingPrepareRecording = async (args: {
  audioRecordingId: string;
  uploadToken: string;
  windowId: string;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.prepareRecording({
    audioRecordingId: args.audioRecordingId,
    uploadToken: args.uploadToken,
    windowId: args.windowId,
  });
};

export const recallBindingStartRecording = async (args: {
  windowId: string;
  uploadToken: string;
  prepared: boolean;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.startRecording({
    windowId: args.windowId,
    uploadToken: args.uploadToken,
    prepared: args.prepared,
  });
};

export const recallBindingPauseRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.pause({ windowId: args.windowId });
};

export const recallBindingResumeRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.resume({ windowId: args.windowId });
};

export const recallBindingStopRecording = async (args: { windowId: string }) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.stop({ windowId: args.windowId });
};

export const recallBindingOnEvent = async ({
  callback,
}: {
  callback: (payload: { event: unknown }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onEvent?.(callback);
};

export const recallBindingOnMeetingEvent = async ({
  callback,
}: {
  callback: (payload: {
    event: {
      window: { id: string; title?: string; url?: string; platform: string };
      info?: {
        audioRecordingId?: string;
        startTime?: string | null;
        endTime?: string | null;
        attendees?: string[] | null;
      };
    };
    kind: RecallMeetingDetectedEventKind;
    debug: boolean;
  }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onMeetingEvent?.(callback);
};

export const recallBindingGetPermissionStatus = async (): Promise<
  Record<RecallPermissionKind, RecallPermissionStatus>
> => {
  const electronApi = getElectronAPI();

  const status = await electronApi?.recall.getPermissionStatus();

  if (!status) {
    return {
      [RecallPermissionKind.Accessibility]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.ScreenCapture]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.Microphone]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.SystemAudio]: RecallPermissionStatus.NotRequested,
      [RecallPermissionKind.FullDiskAccess]: RecallPermissionStatus.NotRequested,
    };
  }

  return status;
};

export const recallBindingRequestPermission = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.requestPermission(permission);
};

export const recallBindingOpenPermissionPane = async ({
  permission,
}: {
  permission: RecallPermissionKind;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.openPermissionPane(permission);
};

export const recallBindingOnPermissionStatus = async ({
  callback,
}: {
  callback: (payload: {
    event: { permission: RecallPermissionKind; status: RecallPermissionStatus };
  }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onPermissionStatus?.(callback);
};

export const recallBindingGetSdkState = async () => {
  const electronApi = getElectronAPI();
  const payload = await electronApi?.recall.getSdkState();

  return (
    payload ?? {
      state: RecallSdkState.Idle,
    }
  );
};

export const recallBindingOnSdkState = async ({
  callback,
}: {
  callback: (payload: { state: RecallSdkState; errorMessage?: string }) => void;
}) => {
  const electronApi = getElectronAPI();
  return electronApi?.recall.onSdkState?.(callback);
};

export const recallBindingTestUnexpectedShutdown = async () => {
  const electronApi = getElectronAPI();
  await electronApi?.recall.testUnexpectedShutdown?.();
};

export const electronBindingSetClientMetadata = async ({
  context,
}: {
  context: Record<string, unknown>;
}) => {
  const electronApi = getElectronAPI();
  await electronApi?.setClientMetadata?.({ context });
};

export const electronBindingClearClientMetadata = async () => {
  const electronApi = getElectronAPI();
  await electronApi?.clearClientMetadata?.();
};

export const electronBindingGetElectronAppInfo = async (): Promise<ElectronAppInfo | null> => {
  const electronApi = getElectronAPI();
  const info = await electronApi?.getElectronAppInfo?.();
  return info ?? null;
};

export const electronBindingImportAppleNotes = async (): Promise<ImportAppleNotesResult> => {
  const electronApi = getElectronAPI();
  return (
    (await electronApi?.importAppleNotes?.()) ?? {
      success: false,
      error: "Not available",
      notes: [],
      notesPerTag: {},
    }
  );
};
